import { SITE_NAME, DEFAULT_OG_IMAGE, absoluteUrl, buildCanonicalUrl } from './siteConfig';

function truncate(text, max = 160) {
  if (!text) return '';
  const cleaned = String(text).replace(/\s+/g, ' ').trim();
  if (cleaned.length <= max) return cleaned;
  return `${cleaned.slice(0, max - 1).trim()}…`;
}

function yearFromDate(dateStr) {
  if (!dateStr) return '';
  const y = new Date(dateStr).getFullYear();
  return Number.isFinite(y) ? String(y) : '';
}

export function homepageMeta({ totalReports, totalActivists } = {}) {
  const reports = totalReports != null ? totalReports : '';
  const activists = totalActivists != null ? totalActivists : '';
  const title = `${SITE_NAME} | Short Seller Activist Intelligence`;
  const description = truncate(
    reports && activists
      ? `${SITE_NAME}: ${reports} activist short seller reports from ${activists} researchers.`
      : `${SITE_NAME}: activist short seller reports and researcher intelligence.`
  );
  return {
    title,
    description,
    canonical: buildCanonicalUrl('/'),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
  };
}

export function researchMeta() {
  const title = `Research | ${SITE_NAME}`;
  const description = truncate(
    `${SITE_NAME} research on activist short selling: event studies, cumulative abnormal returns, and campaign data.`
  );
  return {
    title,
    description,
    canonical: buildCanonicalUrl('/research'),
    ogImage: `${absoluteUrl('')}/phalanx-bg.png`,
    ogType: 'article',
  };
}

export function activistMeta({ name, totalReports, companiesTargeted, latestAttack } = {}) {
  const title = `${name} | Activist Short Seller Profile | ${SITE_NAME}`;
  const description = truncate(
    [
      name,
      totalReports != null ? `${totalReports} reports` : null,
      companiesTargeted != null ? `${companiesTargeted} companies targeted` : null,
      latestAttack ? `latest ${latestAttack}` : null,
    ]
      .filter(Boolean)
      .join(' · ')
  );
  const path = `/activist/${encodeURIComponent(name || '')}`;
  return {
    title,
    description,
    canonical: buildCanonicalUrl(path),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'profile',
  };
}

export function reportMeta({
  activist,
  reportTitle,
  targetCompany,
  ticker,
  publicationDate,
  sector,
} = {}) {
  const year = yearFromDate(publicationDate);
  const companyLabel = targetCompany || reportTitle || '';
  const tickerLabel = ticker ? ` (${ticker})` : '';
  const title = `${companyLabel}${tickerLabel}${year ? ` — ${year}` : ''} | ${activist} | ${SITE_NAME}`;
  const description = truncate(
    [
      activist,
      reportTitle,
      targetCompany && targetCompany !== reportTitle ? targetCompany : null,
      ticker,
      year,
      sector,
    ]
      .filter(Boolean)
      .join(' · ')
  );
  const path = `/report/${encodeURIComponent(activist || '')}/${encodeURIComponent(reportTitle || '')}`;
  return {
    title,
    description,
    canonical: buildCanonicalUrl(path),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'article',
  };
}

export function sectorAnalysisMeta({ sector, reportCount } = {}) {
  if (sector) {
    const title = `${sector} | Activist Short Selling Sector Data | ${SITE_NAME}`;
    const description = truncate(
      reportCount != null
        ? `${sector}: ${reportCount} activist short seller reports in ${SITE_NAME}.`
        : `${sector}: activist short seller reports in ${SITE_NAME}.`
    );
    return {
      title,
      description,
      canonical: buildCanonicalUrl('/analysis'),
      ogImage: DEFAULT_OG_IMAGE,
      ogType: 'website',
      robots: 'noindex, nofollow',
    };
  }
  const title = `Activist Short Selling Analysis | ${SITE_NAME}`;
  const description = truncate(
    `${SITE_NAME} analysis of activist short seller campaigns by sector, company, and timeline.`
  );
  return {
    title,
    description,
    canonical: buildCanonicalUrl('/analysis'),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    robots: 'noindex, nofollow',
  };
}

export function reportsDatabaseMeta({ totalReports } = {}) {
  const title = `Activist Short Seller Reports Database | ${SITE_NAME}`;
  const description = truncate(
    totalReports != null
      ? `${totalReports} activist short seller reports in the ${SITE_NAME} database.`
      : `Activist short seller reports database on ${SITE_NAME}.`
  );
  return {
    title,
    description,
    canonical: buildCanonicalUrl('/reports'),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    robots: 'noindex, nofollow',
  };
}

export function staticPageMeta({ title, description, path, ogImage, robots }) {
  return {
    title: title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`,
    description: truncate(description),
    canonical: buildCanonicalUrl(path),
    ogImage: ogImage || DEFAULT_OG_IMAGE,
    ogType: 'website',
    robots,
  };
}

export function notFoundMeta() {
  return {
    title: `Page Not Found | ${SITE_NAME}`,
    description: `${SITE_NAME} — page not found.`,
    canonical: buildCanonicalUrl('/'),
    ogImage: DEFAULT_OG_IMAGE,
    ogType: 'website',
    robots: 'noindex, nofollow',
  };
}
